// src/components/programs/ProgramForm/Step0Sections/PaxTypesSection.jsx
import React from "react";
import {
    Grid,
    Paper,
    Typography,
    Box,
    IconButton,
    TextField,
    Stack,
    Fade,
    useTheme,
    alpha,
    Chip,
    Divider,
} from "@mui/material";
import {
    Add as AddIcon,
    Remove as RemoveIcon,
    Person as PersonIcon,
    Groups as GroupsIcon,
    ChildCare as ChildIcon,
    Elderly as ElderlyIcon,
} from "@mui/icons-material";

// Icon mapping for different pax types
const getPaxTypeIcon = (label) => {
    const labelLower = (label?.en || label || '').toLowerCase();
    if (labelLower.includes('adult')) return PersonIcon;
    if (labelLower.includes('child')) return ChildIcon;
    if (labelLower.includes('infant')) return ChildIcon;
    if (labelLower.includes('senior')) return ElderlyIcon;
    return PersonIcon;
};

const PaxTypesSection = ({
    formData,
    setFormData,
    errors,
    isArabic,
    activeLang,
    paxTypesOptions,
    loadingPaxTypes,
    onClearError = null,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    // const [focusedId, setFocusedId] = useState(null);

    const sortedPaxTypesOptions = React.useMemo(() => {
        if (!paxTypesOptions) return [];
        return [...paxTypesOptions].sort((a, b) => {
            const getPriority = (label) => {
                const l = (label?.en || label || '').toLowerCase();
                if (l.includes('adult')) return 1;
                if (l.includes('child')) return 2;
                if (l.includes('infant')) return 3;
                return 4;
            };
            return getPriority(a.label) - getPriority(b.label);
        });
    }, [paxTypesOptions]);

    /**
     * Handle count change for pax type
     */
    const handleCountChange = (paxTypeId, paxTypeName, newCount) => {
        const count = Math.max(0, parseInt(newCount) || 0);

        const updatedPaxTypes = [...formData.pax_types];
        const existingIndex = updatedPaxTypes.findIndex(
            (pt) => pt.pax_type_id === paxTypeId
        );

        if (existingIndex >= 0) {
            if (count === 0) {
                updatedPaxTypes.splice(existingIndex, 1);
            } else {
                updatedPaxTypes[existingIndex].count = count;
            }
        } else if (count > 0) {
            updatedPaxTypes.push({
                pax_type_id: paxTypeId,
                pax_type_name: paxTypeName,
                count: count,
            });
        }

        setFormData({
            ...formData,
            pax_types: updatedPaxTypes,
        });

        // Clear pax_types error when user makes changes
        if (onClearError) {
            onClearError(['pax_types']);
        }
    };

    /**
     * Get count for specific pax type
     */
    const getCount = (paxTypeId) => {
        const paxType = formData.pax_types.find((pt) => pt.pax_type_id === paxTypeId);
        return paxType?.count || 0;
    };

    /**
     * Calculate total pax
     */
    const totalPax = formData.pax_types.reduce((sum, pt) => sum + (pt.count || 0), 0);

    /**
     * Quick add presets
     */
    // const handleQuickAdd = (preset) => {
    //     const presets = {
    //         single: { adults: 1, children: 0, infants: 0 },
    //         couple: { adults: 2, children: 0, infants: 0 },
    //         family: { adults: 2, children: 2, infants: 0 },
    //         group: { adults: 10, children: 0, infants: 0 },
    //     };

    //     const selected = presets[preset];
    //     if (!selected) return;

    //     const updatedPaxTypes = paxTypesOptions
    //         .map(paxType => {
    //             const labelLower = (paxType.label?.en || '').toLowerCase();
    //             let count = 0;

    //             if (labelLower.includes('adult')) count = selected.adults || 0;
    //             else if (labelLower.includes('child')) count = selected.children || 0;
    //             else if (labelLower.includes('infant')) count = selected.infants || 0;

    //             return count > 0 ? {
    //                 pax_type_id: paxType.value,
    //                 pax_type_name: paxType.label,
    //                 count: count,
    //             } : null;
    //         })
    //         .filter(Boolean);

    //     setFormData({
    //         ...formData,
    //         pax_types: updatedPaxTypes,
    //     });
    // };

    return (
        <Box>
            {/* Compact Header */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <GroupsIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        {isArabic ? 'أنواع الركاب' : 'Passenger Types'}
                    </Typography>
                </Box>

                {/* Total Badge */}
                {totalPax > 0 && (
                    <Chip
                        icon={<GroupsIcon fontSize="small" />}
                        label={`${totalPax} ${isArabic ? 'راكب' : 'Pax'}`}
                        size="small"
                        sx={{
                            height: 28,
                            fontWeight: 700,
                            bgcolor: alpha(theme.palette.success.main, 0.15),
                            color: 'success.main',
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        }}
                    />
                )}
            </Stack>

            {/* Quick Presets - Compact */}
            {/* <Stack direction="row" spacing={1} sx={{ mb: 2.5 }} flexWrap="wrap" useFlexGap>
                {[
                    { key: 'single', label: isArabic ? '👤 فرد' : '👤 Single' },
                    { key: 'couple', label: isArabic ? '👥 زوجان' : '👥 Couple' },
                    { key: 'family', label: isArabic ? '👨‍👩‍👧‍👦 عائلة' : '👨‍👩‍👧‍👦 Family' },
                    { key: 'group', label: isArabic ? '🎫 مجموعة' : '🎫 Group' },
                ].map(preset => (
                    <Chip
                        key={preset.key}
                        label={preset.label}
                        size="small"
                        // onClick={() => handleQuickAdd(preset.key)}
                        sx={{
                            height: 28,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            bgcolor: isDark ? alpha('#FFFFFF', 0.05) : alpha('#000000', 0.03),
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                borderColor: theme.palette.primary.main,
                                transform: 'translateY(-1px)',
                            },
                        }}
                    />
                ))}
            </Stack> */}

            <Divider sx={{ mb: 2.5 }} />

            {/* Compact Pax Types Grid */}
            <Grid container spacing={1.5}>
                {loadingPaxTypes ? (
                    <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                {isArabic ? 'جاري التحميل...' : 'Loading...'}
                            </Typography>
                        </Box>
                    </Grid>
                ) : (
                    sortedPaxTypesOptions.map((paxType, index) => {
                        const count = getCount(paxType.value);
                        const label = paxType.label?.[activeLang] || paxType.label?.en || paxType.label || '';
                        const Icon = getPaxTypeIcon(paxType.label);
                        const isSelected = count > 0;

                        return (
                            <Grid item xs={12} sm={6} md={4} key={paxType.value}>
                                <Fade in timeout={150 + index * 30}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 2,
                                            border: `1.5px solid ${isSelected
                                                ? theme.palette.primary.main
                                                : alpha(isDark ? '#FFFFFF' : '#000000', 0.08)
                                                }`,
                                            bgcolor: isSelected
                                                ? alpha(theme.palette.primary.main, 0.05)
                                                : isDark
                                                    ? alpha('#FFFFFF', 0.02)
                                                    : '#FFFFFF',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                borderColor: theme.palette.primary.main,
                                                transform: 'translateY(-2px)',
                                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                            },
                                        }}
                                    >
                                        {/* Compact Header */}
                                        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: 1.5,
                                                    bgcolor: isSelected
                                                        ? theme.palette.primary.main
                                                        : alpha(theme.palette.primary.main, 0.08),
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.2s',
                                                }}
                                            >
                                                <Icon
                                                    sx={{
                                                        fontSize: 18,
                                                        color: isSelected ? '#FFFFFF' : 'primary.main',
                                                    }}
                                                />
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                fontWeight={isSelected ? 700 : 600}
                                                color={isSelected ? 'primary.main' : 'text.primary'}
                                                sx={{ flex: 1 }}
                                            >
                                                {label}
                                            </Typography>
                                        </Stack>

                                        {/* Compact Counter */}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{
                                                p: 0.75,
                                                borderRadius: 1.5,
                                                bgcolor: isDark
                                                    ? alpha('#FFFFFF', 0.03)
                                                    : alpha('#000000', 0.02),
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleCountChange(paxType.value, paxType.label, count - 1)
                                                }
                                                disabled={count === 0}
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    bgcolor: count > 0 ? alpha(theme.palette.error.main, 0.08) : 'transparent',
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.error.main, 0.15),
                                                    },
                                                    '&.Mui-disabled': {
                                                        opacity: 0.3,
                                                    },
                                                }}
                                            >
                                                <RemoveIcon sx={{ fontSize: 16 }} />
                                            </IconButton>

                                            <TextField
                                                value={count}
                                                onChange={(e) =>
                                                    handleCountChange(paxType.value, paxType.label, e.target.value)
                                                }
                                                // onFocus={() => setFocusedId(paxType.value)}
                                                // onBlur={() => setFocusedId(null)}
                                                size="small"
                                                type="number"
                                                sx={{
                                                    width: 60,
                                                    '& input': {
                                                        textAlign: 'center',
                                                        fontSize: '0.95rem',
                                                        fontWeight: 700,
                                                        color: isSelected ? 'primary.main' : 'text.primary',
                                                        padding: '6px',
                                                    },
                                                    '& fieldset': {
                                                        border: 'none',
                                                    },
                                                }}
                                                inputProps={{ min: 0 }}
                                            />

                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleCountChange(paxType.value, paxType.label, count + 1)
                                                }
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    bgcolor: alpha(theme.palette.success.main, 0.08),
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.success.main, 0.15),
                                                    },
                                                }}
                                            >
                                                <AddIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Stack>
                                    </Paper>
                                </Fade>
                            </Grid>
                        );
                    })
                )}
            </Grid>

            {/* Compact Summary */}
            {totalPax > 0 && (
                <Fade in timeout={200}>
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 2.5,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.success.main, 0.05),
                            border: `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        }}
                    >
                        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,
                                    bgcolor: alpha(theme.palette.success.main, 0.15),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <GroupsIcon sx={{ fontSize: 24, color: 'success.main' }} />
                            </Box>

                            {/* Total */}
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                                    {isArabic ? 'الإجمالي' : 'Total Passengers'}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} color="success.main">
                                    {totalPax} {isArabic ? 'راكب' : 'Pax'}
                                </Typography>
                            </Box>

                            {/* <Divider orientation="vertical" flexItem /> */}

                            {/* Breakdown */}
                            {/* <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                                {formData.pax_types.map(pt => {
                                    const label = pt.pax_type_name?.[activeLang] || pt.pax_type_name?.en || '';
                                    return (
                                        <Chip
                                            key={pt.pax_type_id}
                                            label={`${label}: ${pt.count}`}
                                            size="small"
                                            sx={{
                                                height: 24,
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                                color: 'success.dark',
                                                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                            }}
                                        />
                                    );
                                })}
                            </Stack> */}
                        </Stack>
                    </Paper>
                </Fade>
            )}

            {/* Compact Error Message */}
            {errors.pax_types && (
                <Fade in>
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.error.main, 0.08),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        }}
                    >
                        <Typography variant="caption" color="error" fontWeight={600}>
                            ⚠️ {errors.pax_types}
                        </Typography>
                    </Box>
                </Fade>
            )}
        </Box>
    );
};

export default PaxTypesSection;
